import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, Box, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { FlowBreadcrumbs, FlowFormCard, SubmitButton, mediaBaseUrl, mediaPreviewStyles } from './FlowLayout';
import { fetchDestinationHierarchy } from './flowHierarchy';

const EditItinerary = () => {
  const { id, tourTypeId } = useParams();
  const navigate = useNavigate();
  const [destinationsId, setDestinationsId] = useState('');
  const [hierarchy, setHierarchy] = useState({
    destinationName: '',
    tourTypeId: '',
    tourTypeName: '',
    continentId: '',
    continentName: ''
  });
  const [tabHeading, setTabHeading] = useState('');
  const [descriptionHeading, setDescriptionHeading] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [currentImage, setCurrentImage] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    axios.get(`https://aaryanholidays.in/backend/itineraries/item/${id}`)
      .then((response) => {
        const itinerary = response.data;
        setDestinationsId(itinerary.destinations_id);
        setTabHeading(itinerary.tab_heading);
        setDescriptionHeading(itinerary.description_heading);
        setDescription(itinerary.description);
        setCurrentImage(itinerary.image);
        return fetchDestinationHierarchy(itinerary.destinations_id);
      })
      .then((response) => {
        if (response) {
          setHierarchy(response);
        }
      })
      .catch((error) => console.error('There was an error fetching the itinerary!', error));
  }, [id]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    const formData = new FormData();
    formData.append('destinations_id', destinationsId);
    formData.append('tab_heading', tabHeading);
    formData.append('description_heading', descriptionHeading);
    formData.append('description', description);
    if (image) {
      formData.append('image', image);
    } else {
      formData.append('currentImage', currentImage);
    }

    try {
      await axios.put(`https://aaryanholidays.in/backend/itineraries/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSnackbar({ open: true, message: 'Itinerary updated successfully.', severity: 'success' });
      navigate(`/manage-destination/itineraries/${tourTypeId}`);
    } catch (error) {
      console.error('There was an error updating the itinerary!', error);
      setSnackbar({ open: true, message: 'There was an error updating the itinerary.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <FlowBreadcrumbs items={[{ label: 'Tour Management', to: '/manage-continents' }, { label: hierarchy.continentName || 'Continent', to: hierarchy.continentId ? `/manage-tour-type/${hierarchy.continentId}` : undefined }, { label: hierarchy.tourTypeName || 'Country', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined }, { label: hierarchy.destinationName || 'Package', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined }, { label: 'Itineraries', to: destinationsId ? `/manage-destination/itineraries/${destinationsId}` : undefined }, { label: 'Edit Itinerary' }]} />
      <FlowFormCard title="Edit Itinerary" subtitle="Update the content structure and supporting visual while keeping the package flow clean and easy to scan.">
        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField label="Tab Heading" value={tabHeading} onChange={(e) => setTabHeading(e.target.value)} fullWidth required />
            <TextField label="Description Heading" value={descriptionHeading} onChange={(e) => setDescriptionHeading(e.target.value)} fullWidth required />
            <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={5} required />
            <Box>
              <input id="edit-itinerary-image" type="file" hidden accept="image/*" onChange={(e) => {
                const file = e.target.files[0];
                setImage(file);
                setImagePreview(file ? URL.createObjectURL(file) : null);
              }} />
              <label htmlFor="edit-itinerary-image">
                <SubmitButton component="span" variant="outlined">
                  Replace Image
                </SubmitButton>
              </label>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {currentImage ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: '#64748b' }}>Current Image</Typography>
                  <img src={`${mediaBaseUrl}${currentImage}`} alt="Current itinerary" style={mediaPreviewStyles} />
                </Box>
              ) : null}
              {imagePreview ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: '#64748b' }}>New Preview</Typography>
                  <img src={imagePreview} alt="New itinerary preview" style={mediaPreviewStyles} />
                </Box>
              ) : null}
            </Stack>
            <SubmitButton type="submit" variant="contained" loading={submitting}>
              Update Itinerary
            </SubmitButton>
          </Stack>
        </form>
      </FlowFormCard>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditItinerary;
